import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  bookingOfferCheckMessages,
  hasPriceBreakdown,
  offerCancellationLabel,
  type BookingOfferCheckResultType,
  type BookingOfferPreviewType,
} from '@/types/types_bookingRequest';
import { bookingOfferPreview, respondToBookingOffer } from '@/util/callable_functions';
import { formatCalendarDate, formatDeadlineDayJst, formatDeadlineJst, formatJpy } from '@/util/util_format';

/** What the page is doing, rather than what the offer says. */
type PageStateType = 'loading' | 'ready' | 'answering' | 'answered' | 'unavailable';

/**
 * Answers an emailed offer: `/booking/:requestId?t=<token>`.
 *
 * Rendered outside the AuthGate (see App.tsx), because requiring a sign-in here
 * would defeat the link. A concierge gets this on a phone, at a desk they share,
 * possibly on a shift after the one that submitted the request — the token in
 * the URL is what authorises them, and it is checked again on the server at the
 * moment they press the button.
 *
 * Deliberately two buttons and nothing else. Everything that could be negotiated
 * — a different date, a different price — is a phone call to Eighty Days, and
 * offering a text box here would imply otherwise.
 */
export function PageBookingOffer() {
  const { requestId } = useParams<{ requestId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';

  const [state, setState] = useState<PageStateType>('loading');
  const [offer, setOffer] = useState<BookingOfferPreviewType | null>(null);
  // Never 'ok' — that case is `state`, not a problem to explain.
  const [problem, setProblem] = useState<Exclude<BookingOfferCheckResultType, 'ok'> | null>(null);
  const [answer, setAnswer] = useState<'confirm' | 'decline' | null>(null);
  // Which of the two was pressed, so the spinner appears on that one. `state`
  // alone says an answer is in flight, not which button sent it.
  const [answering, setAnswering] = useState<'confirm' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A missing id or token is not special-cased here: the callable answers
  // 'not-found' for either, so a malformed URL costs one wasted round trip and
  // buys a single code path for "this link does not work".
  useEffect(() => {
    let cancelled = false;
    bookingOfferPreview({ requestId: requestId ?? '', token })
      .then((res) => {
        if (cancelled) return;
        setOffer(res.data);
        if (res.data.result === 'ok') {
          setState('ready');
        } else {
          setProblem(res.data.result);
          setState('unavailable');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('bookingOfferPreview failed', e);
        setError('We could not load this offer. Please try again, or contact Eighty Days.');
        setState('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [requestId, token]);

  async function respond(choice: 'confirm' | 'decline') {
    if (!requestId || state === 'answering') return;
    setState('answering');
    setAnswering(choice);
    setError(null);
    try {
      const res = await respondToBookingOffer({ requestId, token, answer: choice });
      if (res.data.result === 'ok') {
        setAnswer(choice);
        setState('answered');
      } else {
        // The offer moved under them — the hold lapsed while the page sat open,
        // or somebody at the partner answered on another device.
        setProblem(res.data.result);
        setState('unavailable');
      }
    } catch (e) {
      console.error('respondToBookingOffer failed', e);
      setError('We could not record your answer. Please try again, or contact Eighty Days.');
      setState('ready');
    } finally {
      setAnswering(null);
    }
  }

  return (
    <div className='tw:min-h-screen tw:bg-ed-ink tw:font-sans tw:text-ed-cream'>
      <div className='ed-bg-pattern' />
      <div className='ed-ink-wash' />

      <div className='tw:relative tw:mx-auto tw:flex tw:min-h-screen tw:max-w-xl tw:flex-col tw:justify-center tw:px-6 tw:py-16'>
        <div className='tw:mb-10 tw:flex tw:items-center tw:gap-3'>
          <img src='/eightydays_balloon.png' alt='' className='tw:h-9 tw:w-auto tw:object-contain' />
          <span className='tw:font-serif tw:text-xl tw:tracking-wider tw:text-white tw:uppercase'>Eighty Days</span>
        </div>

        <div className='tw:rounded-sm tw:border tw:border-white/10 tw:bg-white/5 tw:p-8 tw:backdrop-blur-md'>
          {state === 'loading' && (
            <p className='tw:flex tw:items-center tw:gap-3 tw:text-sm tw:text-ed-cream/70'>
              <Loader2 className='tw:size-4 tw:animate-spin' />
              Loading this offer…
            </p>
          )}

          {state === 'unavailable' && (
            <>
              <h1 className='tw:font-serif tw:text-2xl tw:text-white'>This offer is no longer open</h1>
              <p className='tw:mt-4 tw:text-sm tw:text-ed-cream/70'>
                {error ?? (problem ? bookingOfferCheckMessages[problem] : '')}
              </p>
              {offer?.experienceName && (
                <p className='tw:mt-6 tw:text-xs tw:tracking-wider tw:text-ed-cream/50 tw:uppercase'>
                  {offer.experienceName} · {formatCalendarDate(offer.confirmedDate)}
                  {/* The one state with nothing left to press, and so the one
                      where the concierge is most likely to be picking up the
                      phone. Give them the thing to quote. */}
                  {offer.reference && <> · {offer.reference}</>}
                </p>
              )}
            </>
          )}

          {state === 'answered' && (
            <>
              <h1 className='tw:font-serif tw:text-2xl tw:text-white'>
                {answer === 'confirm' ? 'Booking confirmed' : 'Date released'}
              </h1>
              <p className='tw:mt-4 tw:text-sm tw:text-ed-cream/70'>
                {answer === 'confirm'
                  ? 'Thank you. We have told the artisan, and you will receive the details shortly.'
                  : 'Thank you for letting us know. The date has been released.'}
              </p>
              {offer && <OfferDetails offer={offer} showDeadline={false} />}
            </>
          )}

          {(state === 'ready' || state === 'answering') && offer && (
            <>
              <p className='tw:text-xs tw:tracking-[0.2em] tw:text-ed-gold tw:uppercase'>Date held for you</p>
              <h1 className='tw:mt-3 tw:font-serif tw:text-2xl tw:text-white'>{offer.experienceName}</h1>
              <OfferDetails offer={offer} showDeadline />

              {error && <p className='tw:mt-6 tw:text-sm tw:text-red-300'>{error}</p>}

              <div className='tw:mt-8 tw:flex tw:flex-col tw:gap-3 tw:sm:flex-row'>
                <Button
                  onClick={() => void respond('confirm')}
                  disabled={state === 'answering'}
                  loading={answering === 'confirm'}
                  className='tw:flex-1'
                >
                  Confirm this booking
                </Button>
                <Button
                  variant='outline'
                  onClick={() => void respond('decline')}
                  disabled={state === 'answering'}
                  loading={answering === 'decline'}
                  // This page is dark by design and sits outside the app's light theme, so the
                  // outline variant's `bg-background` would be near-white under near-white text.
                  className='tw:flex-1 tw:border-white/30 tw:bg-transparent tw:text-white tw:hover:bg-white/10 tw:hover:text-white'
                >
                  Decline
                </Button>
              </div>

              <p className='tw:mt-6 tw:text-xs tw:text-ed-cream/50'>
                Confirming commits the booking at the price shown. To change the date or the number of
                guests, please contact Eighty Days rather than answering here.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** The facts of an offer, shown identically before and after answering. */
function OfferDetails({ offer, showDeadline }: { offer: BookingOfferPreviewType; showDeadline: boolean }) {
  // The words are the server's, not this page's — the same ones the offer email
  // used. See `offerCancellationLabel`.
  const cancellation = offerCancellationLabel(offer.cancellation, formatDeadlineDayJst);

  // Every line of what is being charged, where the offer is more than the
  // experience — the same rows, in the same order, as the email that linked
  // here. The experience's own line stays labelled 'Price': the page is headed
  // with its name already.
  const price: [string, string][] = hasPriceBreakdown(offer)
    ? [
      ...(offer.priceLines ?? []).map((line, i): [string, string] => [
        i === 0 ? 'Price' : line.label,
        formatJpy(line.amount),
      ]),
      ['Total', formatJpy(offer.agreedPrice ?? 0)],
    ]
    : [['Price', typeof offer.agreedPrice === 'number' ? formatJpy(offer.agreedPrice) : '—']];

  const rows: [string, string][] = [
    ['Date', formatCalendarDate(offer.confirmedDate)],
    ['Guests', String(offer.guests ?? '—')],
    ...price,
    // Kept after answering as well as before: it is the term the partner is most
    // likely to come back about, and this page is their only copy of it until
    // the confirmation email arrives.
    ...(cancellation ? ([['Cancellation terms', cancellation]] as [string, string][]) : []),
    ...(showDeadline ? ([['Hold expires', formatDeadlineJst(offer.heldUntil)]] as [string, string][]) : []),
  ];

  return (
    <>
      <dl className='tw:mt-6 tw:space-y-3'>
        {/* Keyed by position rather than by label: two services on one offer may
            legitimately be called the same thing. */}
        {rows.map(([label, value], i) => (
          <div key={i} className='tw:flex tw:justify-between tw:gap-6 tw:border-b tw:border-white/5 tw:pb-3'>
            <dt className='tw:text-xs tw:tracking-wider tw:text-ed-cream/50 tw:uppercase'>{label}</dt>
            <dd className='tw:text-right tw:text-sm tw:text-white'>{value}</dd>
          </div>
        ))}
      </dl>

      {/* The note the desk sent with this offer, in its own block rather than as
          a row: it is written in sentences, and squeezing it into the right-hand
          column of a definition list would make it the hardest thing here to
          read. `whitespace-pre-line` keeps the paragraphs as they were typed. */}
      {offer.publicNotes && (
        <p className='tw:mt-6 tw:border-l-2 tw:border-ed-gold/40 tw:pl-4 tw:text-sm tw:whitespace-pre-line tw:text-ed-cream/80'>
          {offer.publicNotes}
        </p>
      )}

      {/* Signed off the way the offer email is, in the same place and the same
          quiet weight: it is what to quote if anything about this booking has to
          be discussed, not a fact about the offer to weigh alongside the price. */}
      {offer.reference && (
        <p className='tw:mt-6 tw:text-xs tw:text-ed-cream/40'>Reference {offer.reference}</p>
      )}
    </>
  );
}
